import { Button, DatePicker, Input, Modal, NumberInput, Notice, Select, SelectButton, Textarea } from '@bennows/boows-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFailure } from '../../lib/api';
import { endOfViennaDay, formatNumber } from '../../lib/format';
import { useToast } from '../../lib/toasts';
import { ADJUSTMENT_REASONS, useBillingActions, type AdjustmentReason } from './useTenantBilling';

const FORM_ID = 'credit-adjustment';
/** As the API and the platform: a reason of 3 to 500 characters, at most 100,000 credits per booking. */
const REASON_MIN = 3;
const REASON_MAX = 500;
const AMOUNT_MAX = 100_000;

const FAILURES: Record<string, string> = {
  'insufficient-credits': 'tenantBilling.adjust.insufficient',
  'tenant-exempt': 'tenantBilling.adjust.exempt',
  'tenant-not-billed': 'tenantBilling.unlinkedText',
  'platform-unavailable': 'tenantBilling.adjust.platformDown',
  'idempotency-key-in-use': 'tenantBilling.adjust.inProgress',
  network: 'common.errorNetwork',
};

type Direction = 'grant' | 'deduct';

/**
 * Grant or deduct credits with a reason code and reason (REQ-140-020). A
 * deduction also asks for the company name typed out (REQ-140-024). One
 * idempotency key per opened dialog, so a repeated submit books once.
 */
export function CreditAdjustmentDialog({ tenantId, tenantName, available, onClose }: { readonly tenantId: string; readonly tenantName: string; readonly available: number; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { adjust } = useBillingActions(tenantId);
  const [key] = useState(() => crypto.randomUUID());
  const [direction, setDirection] = useState<Direction>('grant');
  const [amount, setAmount] = useState<number | null>(null);
  const [reasonCode, setReasonCode] = useState<AdjustmentReason>('goodwill');
  const [reason, setReason] = useState('');
  const [expiry, setExpiry] = useState('');
  const [typedName, setTypedName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const deduct = direction === 'deduct';
  const amountInvalid = amount === null || !Number.isInteger(amount) || amount < 1 || amount > AMOUNT_MAX;
  const reasonMissing = reason.trim().length < REASON_MIN;
  const nameMismatch = deduct && typedName.trim() !== tenantName;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (amountInvalid || reasonMissing || nameMismatch) return;
    const signed = deduct ? -amount : amount;
    adjust.mutate(
      { amount: signed, reasonCode, reason: reason.trim(), expiresAt: !deduct && expiry ? endOfViennaDay(expiry) : null, key },
      { onSuccess: () => (toast(t(deduct ? 'tenantBilling.adjust.deducted' : 'tenantBilling.adjust.granted', { amount: formatNumber(amount), name: tenantName })), onClose()) },
    );
  };

  const failure = adjust.error instanceof ApiFailure ? (FAILURES[adjust.error.slug] ?? 'common.errorUnexpected') : adjust.error ? 'common.errorUnexpected' : null;
  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenantBilling.adjust.title')}
      description={tenantName}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} variant={deduct ? 'danger' : 'primary'} loading={adjust.isPending}>
            {t(deduct ? 'tenantBilling.adjust.submitDeduct' : 'tenantBilling.adjust.submitGrant')}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="ui-console-form" onSubmit={onSubmit} noValidate>
        <SelectButton
          label={t('tenantBilling.adjust.direction')}
          value={direction}
          options={(['grant', 'deduct'] as const).map((id) => ({ id, label: t(`tenantBilling.adjust.directions.${id}`) }))}
          onChange={(id) => setDirection(id as Direction)}
        />
        <NumberInput
          label={t('tenantBilling.adjust.amount')}
          value={amount}
          onChange={setAmount}
          min={1}
          max={AMOUNT_MAX}
          precision={0}
          required
          invalid={submitted && amountInvalid}
          help={submitted && amountInvalid ? t('tenantBilling.adjust.amountInvalid') : t('tenantBilling.adjust.available', { amount: formatNumber(available) })}
        />
        <Select
          label={t('tenantBilling.adjust.reasonCode')}
          options={ADJUSTMENT_REASONS.map((id) => ({ id, label: t(`tenantBilling.reasons.${id}`) }))}
          value={reasonCode}
          onChange={(id) => setReasonCode(id as AdjustmentReason)}
        />
        <Textarea
          label={t('tenantActions.reason')}
          value={reason}
          onChange={setReason}
          rows={3}
          maxLength={REASON_MAX}
          required
          invalid={submitted && reasonMissing}
          help={submitted && reasonMissing ? t('tenantActions.reasonMissing') : t('tenantBilling.adjust.reasonHelp')}
        />
        {deduct ? (
          <>
            <Notice tone="warning" role="none">
              {t('tenantBilling.adjust.deductEffects')}
            </Notice>
            <Input
              label={t('tenantActions.suspend.confirmName', { name: tenantName })}
              value={typedName}
              onChange={setTypedName}
              required
              invalid={submitted && nameMismatch}
              help={submitted && nameMismatch ? t('tenantActions.suspend.nameMismatch') : undefined}
            />
          </>
        ) : (
          <DatePicker label={t('tenantBilling.adjust.expiry')} value={expiry} onChange={setExpiry} help={t('tenantBilling.adjust.expiryHelp')} />
        )}
        {failure ? (
          <p className="ui-auth__message" role="alert">
            {t(failure)}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
